(() => {
  'use strict';

  const COOKIE_NAME = 'nw_cookie_consent';
  const STORAGE_KEY = 'nw_cookie_consent_v1';
  const SESSION_FALLBACK_KEY = 'nw_cookie_consent_session_v1';
  const CONSENT_VERSION = 1;
  const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

  let marketingScriptsLoaded = false;
  let analyticsScriptsLoaded = false;

  function readCookie(name) {
    try {
      const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (_) {
      return null;
    }
  }

  function writeCookie(name, value, maxAge) {
    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
    } catch (_) {}
  }

  function deleteCookie(name) {
    try {
      document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
      const hostParts = window.location.hostname.split('.');
      if (hostParts.length >= 2) {
        const rootDomain = '.' + hostParts.slice(-2).join('.');
        document.cookie = `${name}=; path=/; domain=${rootDomain}; max-age=0; SameSite=Lax`;
      }
    } catch (_) {}
  }

  function parseConsent(raw) {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (typeof parsed.analytics !== 'boolean' || typeof parsed.marketing !== 'boolean') return null;
      return {
        v: CONSENT_VERSION,
        necessary: true,
        preferences: Boolean(parsed.preferences),
        analytics: Boolean(parsed.analytics),
        marketing: Boolean(parsed.marketing),
        mode: parsed.mode || 'custom',
        updatedAt: parsed.updatedAt || new Date().toISOString()
      };
    } catch (_) {
      return null;
    }
  }

  function getSavedConsent() {
    const fromCookie = parseConsent(readCookie(COOKIE_NAME));
    if (fromCookie) return fromCookie;
    try {
      const fromLocal = parseConsent(localStorage.getItem(STORAGE_KEY));
      if (fromLocal) {
        // Restore first-party cookie if localStorage survived
        writeCookie(COOKIE_NAME, JSON.stringify(fromLocal), MAX_AGE_SECONDS);
        return fromLocal;
      }
    } catch (_) {}
    try {
      const fromSession = parseConsent(sessionStorage.getItem(SESSION_FALLBACK_KEY));
      if (fromSession) return fromSession;
    } catch (_) {}
    return null;
  }

  function persistConsentRecord(state) {
    const serialized = JSON.stringify(state);
    writeCookie(COOKIE_NAME, serialized, MAX_AGE_SECONDS);
    try {
      localStorage.setItem(STORAGE_KEY, serialized);
    } catch (_) {}
    try {
      sessionStorage.setItem(SESSION_FALLBACK_KEY, serialized);
    } catch (_) {}
  }

  function clearNonEssentialTrackingCookies() {
    const cookiesToClear = ['_ga', '_gid', '_gat', '_gcl_au', '_uetvid', '_uetsid', '_fbp', '_fbc'];
    cookiesToClear.forEach(deleteCookie);
    try {
      const allCookies = document.cookie.split(';');
      for (const entry of allCookies) {
        const eqPos = entry.indexOf('=');
        const name = (eqPos > -1 ? entry.slice(0, eqPos) : entry).trim();
        if (name.startsWith('_ga_') || name.startsWith('_gac_') || name.startsWith('_uet')) {
          deleteCookie(name);
        }
      }
    } catch (_) {}
  }

  function syncShopifyCustomerPrivacy(state) {
    const applyToApi = () => {
      try {
        if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
          window.Shopify.customerPrivacy.setTrackingConsent(
            {
              analytics: Boolean(state.analytics),
              marketing: Boolean(state.marketing),
              preferences: Boolean(state.preferences),
              sale_of_data: Boolean(state.marketing)
            },
            () => {
              document.dispatchEvent(
                new CustomEvent('visitorConsentCollected', {
                  detail: {
                    analyticsAllowed: Boolean(state.analytics),
                    marketingAllowed: Boolean(state.marketing),
                    preferencesAllowed: Boolean(state.preferences),
                    saleOfDataAllowed: Boolean(state.marketing)
                  }
                })
              );
            }
          );
          return;
        }
      } catch (_) {}

      document.dispatchEvent(
        new CustomEvent('visitorConsentCollected', {
          detail: {
            analyticsAllowed: Boolean(state.analytics),
            marketingAllowed: Boolean(state.marketing),
            preferencesAllowed: Boolean(state.preferences),
            saleOfDataAllowed: Boolean(state.marketing)
          }
        })
      );
    };

    if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
      applyToApi();
    } else if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        () => applyToApi()
      );
    } else {
      applyToApi();
    }
  }

  function updateGoogleConsent(state) {
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = window.gtag || gtag;

    window.gtag('consent', 'update', {
      ad_storage: state.marketing ? 'granted' : 'denied',
      ad_user_data: state.marketing ? 'granted' : 'denied',
      ad_personalization: state.marketing ? 'granted' : 'denied',
      analytics_storage: state.analytics ? 'granted' : 'denied',
      functionality_storage: state.preferences ? 'granted' : 'denied',
      personalization_storage: state.preferences ? 'granted' : 'denied',
      security_storage: 'granted'
    });
  }

  function loadGoogleTagIfNeeded(state) {
    if (!state.analytics && !state.marketing) return;
    if (analyticsScriptsLoaded) return;
    analyticsScriptsLoaded = true;

    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js?id=AW-419847743"]')) {
      const s = document.createElement('script');
      s.async = true;
      s.src = 'https://www.googletagmanager.com/gtag/js?id=AW-419847743';
      document.head.appendChild(s);
    }
    window.gtag('js', new Date());
    window.gtag('config', 'AW-419847743');
  }

  function loadMarketingScriptsIfNeeded(state) {
    window.uetq = window.uetq || [];
    window.uetq.push('consent', 'update', {
      ad_storage: state.marketing ? 'granted' : 'denied'
    });

    if (!state.marketing) return;
    if (marketingScriptsLoaded) return;
    marketingScriptsLoaded = true;

    // Microsoft / Bing UET (97201844)
    if (!document.querySelector('script[src*="bat.bing.com/bat.js"]')) {
      (function(w, d, t, r, u) {
        var f, n, i;
        w[u] = w[u] || [];
        f = function() {
          var o = { ti: '97201844', enableAutoSpaTracking: true };
          o.q = w[u];
          w[u] = new UET(o);
          w[u].push('pageLoad');
        };
        n = d.createElement(t);
        n.src = r;
        n.async = 1;
        n.onload = n.onreadystatechange = function() {
          var s = this.readyState;
          s && s !== 'loaded' && s !== 'complete' || (f(), n.onload = n.onreadystatechange = null);
        };
        i = d.getElementsByTagName(t)[0];
        i.parentNode.insertBefore(n, i);
      })(window, document, 'script', '//bat.bing.com/bat.js', 'uetq');
    }

    // PartnerTrack script
    if (!document.querySelector('script[src*="partnertrackshopify.dk/js/register.js"]')) {
      const pt = document.createElement('script');
      pt.async = true;
      pt.src = 'https://partnertrackshopify.dk/js/register.js';
      document.body.appendChild(pt);
    }
  }

  function applyConsentSideEffects(state) {
    updateGoogleConsent(state);
    loadGoogleTagIfNeeded(state);
    loadMarketingScriptsIfNeeded(state);
    if (!state.analytics && !state.marketing) {
      clearNonEssentialTrackingCookies();
    }
    syncShopifyCustomerPrivacy(state);
  }

  const root = document.getElementById('nw-cookie-consent');
  const detailsPanel = document.getElementById('nw-cookie-details');
  const backdrop = root ? root.querySelector('[data-nw-cookie-backdrop]') : null;
  const toggleBtn = root ? root.querySelector('[data-nw-cookie-toggle-details]') : null;
  const toggleLabel = root ? root.querySelector('[data-nw-cookie-toggle-label]') : null;
  const acceptAllBtn = root ? root.querySelector('[data-nw-cookie-accept-all]') : null;
  const rejectBtn = root ? root.querySelector('[data-nw-cookie-reject]') : null;
  const saveBtn = root ? root.querySelector('[data-nw-cookie-save]') : null;
  const statusText = root ? root.querySelector('[data-nw-cookie-status-text]') : null;
  const prefInput = root ? root.querySelector('[data-nw-cookie-input="preferences"]') : null;
  const statInput = root ? root.querySelector('[data-nw-cookie-input="analytics"]') : null;
  const mktInput = root ? root.querySelector('[data-nw-cookie-input="marketing"]') : null;

  function formatStatusSummary(state) {
    if (!state) return '';
    if (state.preferences && state.analytics && state.marketing) {
      return 'Aktuelt valg: Alle cookies accepteret.';
    }
    if (!state.preferences && !state.analytics && !state.marketing) {
      return 'Aktuelt valg: Kun nødvendige cookies.';
    }
    const active = ['Nødvendige'];
    if (state.preferences) active.push('Funktionelle');
    if (state.analytics) active.push('Statistiske');
    if (state.marketing) active.push('Marketing');
    return 'Aktuelt valg: ' + active.join(', ') + '.';
  }

  function syncInputsFromState(state) {
    const current = state || getSavedConsent();
    if (prefInput) prefInput.checked = current ? Boolean(current.preferences) : false;
    if (statInput) statInput.checked = current ? Boolean(current.analytics) : false;
    if (mktInput) mktInput.checked = current ? Boolean(current.marketing) : false;
    if (statusText) statusText.textContent = formatStatusSummary(current);
  }

  function setDetailsExpanded(expanded) {
    if (!root || !detailsPanel || !toggleBtn) return;
    detailsPanel.hidden = !expanded;
    toggleBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    root.classList.toggle('nw-cookie--expanded', expanded);
    root.setAttribute('aria-modal', expanded ? 'true' : 'false');
    if (backdrop) backdrop.hidden = !expanded;
    if (toggleLabel) {
      toggleLabel.textContent = expanded ? 'Skjul valg' : 'Tilpas valg';
    }
  }

  function showBanner(expandDetails = false) {
    if (!root) return;
    syncInputsFromState(getSavedConsent());
    root.hidden = false;
    document.body.classList.add('nw-consent-open');
    setDetailsExpanded(Boolean(expandDetails));
  }

  function hideBanner() {
    if (!root) return;
    setDetailsExpanded(false);
    root.hidden = true;
    document.body.classList.remove('nw-consent-open');
  }

  function commitConsent(choices, mode) {
    const state = {
      v: CONSENT_VERSION,
      necessary: true,
      preferences: Boolean(choices.preferences),
      analytics: Boolean(choices.analytics),
      marketing: Boolean(choices.marketing),
      mode: mode || 'custom',
      updatedAt: new Date().toISOString()
    };
    persistConsentRecord(state);
    syncInputsFromState(state);
    hideBanner();
    applyConsentSideEffects(state);
    return state;
  }

  function acceptAll() {
    return commitConsent({ preferences: true, analytics: true, marketing: true }, 'all');
  }

  function rejectNonEssential() {
    return commitConsent({ preferences: false, analytics: false, marketing: false }, 'necessary');
  }

  function saveCustomChoices() {
    return commitConsent(
      {
        preferences: Boolean(prefInput && prefInput.checked),
        analytics: Boolean(statInput && statInput.checked),
        marketing: Boolean(mktInput && mktInput.checked)
      },
      'custom'
    );
  }

  function resetConsentForTesting() {
    deleteCookie(COOKIE_NAME);
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
    try { sessionStorage.removeItem(SESSION_FALLBACK_KEY); } catch (_) {}
    clearNonEssentialTrackingCookies();
    syncInputsFromState(null);
    showBanner(false);
    return getStatusSnapshot();
  }

  function getStatusSnapshot() {
    const saved = getSavedConsent();
    return {
      hasSavedChoice: Boolean(saved),
      bannerVisible: Boolean(root && !root.hidden),
      detailsExpanded: Boolean(detailsPanel && !detailsPanel.hidden),
      savedState: saved,
      cookiePresent: Boolean(readCookie(COOKIE_NAME)),
      localStoragePresent: (() => {
        try { return Boolean(localStorage.getItem(STORAGE_KEY)); } catch (_) { return false; }
      })(),
      shopifyTrackingConsent: window.Shopify?.customerPrivacy?.getTrackingConsent?.() || null,
      shopifyAnalyticsAllowed: window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() ?? null,
      shopifyMarketingAllowed: window.Shopify?.customerPrivacy?.marketingAllowed?.() ?? null
    };
  }

  // Register Fudge debug controls synchronously (preview-only; no-ops on live store)
  window.__fudgeDebug?.action('Vis cookieboks', () => showBanner(false));
  window.__fudgeDebug?.action('Åbn cookieindstillinger (tilpas)', () => showBanner(true));
  window.__fudgeDebug?.action('Simuler: Accepter alle', () => acceptAll());
  window.__fudgeDebug?.action('Simuler: Kun nødvendige', () => rejectNonEssential());
  window.__fudgeDebug?.action('Nulstil cookievalg (ny besøgende)', () => resetConsentForTesting());
  window.__fudgeDebug?.command('cookieConsentState', () => getStatusSnapshot());
  window.__fudgeDebug?.command('openCookieSettings', (expand) => { showBanner(Boolean(expand)); return getStatusSnapshot(); });
  window.__fudgeDebug?.command('setCookieConsent', (modeOrObj) => {
    if (modeOrObj === 'all') return acceptAll();
    if (modeOrObj === 'necessary') return rejectNonEssential();
    if (modeOrObj && typeof modeOrObj === 'object') return commitConsent(modeOrObj, 'custom');
    return getStatusSnapshot();
  });
  window.__fudgeDebug?.command('resetCookieConsent', () => resetConsentForTesting());

  // Wire UI buttons
  if (acceptAllBtn) acceptAllBtn.addEventListener('click', acceptAll);
  if (rejectBtn) rejectBtn.addEventListener('click', rejectNonEssential);
  if (saveBtn) saveBtn.addEventListener('click', saveCustomChoices);

  if (toggleBtn) {
    toggleBtn.addEventListener('click', () => {
      const isExpanded = detailsPanel && !detailsPanel.hidden;
      setDetailsExpanded(!isExpanded);
    });
  }

  if (backdrop) {
    backdrop.addEventListener('click', () => {
      if (getSavedConsent()) {
        hideBanner();
      } else {
        setDetailsExpanded(false);
      }
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root && !root.hidden) {
      if (getSavedConsent()) {
        hideBanner();
      } else if (detailsPanel && !detailsPanel.hidden) {
        setDetailsExpanded(false);
      }
    }
  });

  // Wire any trigger links/buttons on the page (e.g., footer "Cookieindstillinger" or #cookieindstillinger)
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-nw-cookie-open], a[href="#cookieindstillinger"]');
    if (!trigger) return;
    event.preventDefault();
    showBanner(true);
  });

  // Expose public helper for opening settings
  window.NordicwoodsCookieConsent = Object.freeze({
    open: () => showBanner(false),
    openSettings: () => showBanner(true),
    acceptAll,
    rejectNonEssential,
    getState: getStatusSnapshot,
    reset: resetConsentForTesting
  });

  // Initialize on page load
  const existingConsent = getSavedConsent();
  if (existingConsent) {
    hideBanner();
    applyConsentSideEffects(existingConsent);
  } else {
    showBanner(false);
  }

  if (window.location.hash === '#cookieindstillinger') {
    showBanner(true);
  }
})();
